import React from "react";
import { ToastProps } from "react-toastify/dist/types";
import { cva } from "class-variance-authority";
import { cn } from "@/utils/cssHelpers";
import {
  CheckCircleIcon,
  CloseIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@/icons/primary";

const notificationVariants = cva(
  "border border-gray-200 shadow-lg rounded-base px-5 py-4 flex gap-4 justify-between items-start max-w-sm w-full mb-4",
  {
    variants: {
      intent: {
        success: "",
        warning: "",
        danger: "",
        info: "",
        default: "",
      },
    },
    defaultVariants: {
      intent: "default",
    },
  },
);

const notificationIconVariants = cva("text-2xl flex", {
  variants: {
    intent: {
      success: "!text-green-500",
      warning: "!text-yellow-500",
      danger: "!text-red-500",
      info: "!text-blue-500",
      default: "",
    },
  },
  defaultVariants: {
    intent: "default",
  },
});

const INTENT_ICONS: Record<string, any> = {
  success: CheckCircleIcon,
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
  danger: XCircleIcon,
  default: null,
};

export type CaNotificationProps = {
  intent?: "success" | "warning" | "danger" | "info" | "default" | null;
  title?: string | null;
  description: string;
  closeToast?: ToastProps["closeToast"];
};

function CaNotification({ title, description, intent, closeToast }: CaNotificationProps) {
  const Icon = intent ? INTENT_ICONS[intent] : INTENT_ICONS["default"];

  return (
    <div className={cn(notificationVariants({ intent }))}>
      <div className='flex items-start justify-start gap-4 flex-1'>
        {Icon && (
          <div className='h-full'>
            <Icon className={cn(notificationIconVariants({ intent }))} />
          </div>
        )}
        <div className='text-sm font-sans-serif'>
          {title && <div className='font-medium text-gray-900'>{title}</div>}
          <div className='text-gray-600'>{description}</div>
        </div>
      </div>
      <button
        type='button'
        onClick={closeToast}
        className='border-none bg-transparent p-0 m-0 cursor-pointer focus:outline-none'
      >
        <CloseIcon className='text-gray-400 hover:text-gray-500 flex items-start justify-center text-xl' />
      </button>
    </div>
  );
}

CaNotification.defaultProps = {
  intent: "default",
  title: null,
};

export default CaNotification;
